import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';
import { MultiSelect } from "primereact/multiselect";
import FileDropzone from "../../../components/FileDropzone.jsx";
import ImageUpload from "../../../components/ImageUpload";
import GoogleMapSelector from "../../../components/GoogleMapSelector";
import './PartnerForm.css';

const PartnerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [categories, setCategories] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [coverFiles, setCoverFiles] = useState([]);
  const [oldCoverImages, setOldCoverImages] = useState([]);
  const [documentFiles, setDocumentFiles] = useState({});
  const [mapLocation, setMapLocation] = useState(null);

  const documentFields = [
    { key: 'trade_license', label: 'Trade License' },
    { key: 'emirates_id', label: 'Emirates ID' },
    { key: 'vat_certificate', label: 'VAT Certificate' },
    { key: 'passport_copy', label: 'Passport Copy' },
  ];

  const emiratesOptions = [
    { label: 'Abu Dhabi', value: 'Abu Dhabi' },
    { label: 'Dubai', value: 'Dubai' },
    { label: 'Sharjah', value: 'Sharjah' },
    { label: 'Ajman', value: 'Ajman' },
    { label: 'Umm Al Quwain', value: 'Umm Al Quwain' },
    { label: 'Ras Al Khaimah', value: 'Ras Al Khaimah' },
    { label: 'Fujairah', value: 'Fujairah' },
  ];
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    business_type: '',
    owner_name: '',
    email: '',
    password: '',
    phone: '',
    logo: null,
    about: '',
    website: '',
    address: '',
    lat: '',
    lng: '',
    categories: [],
    emirates: [],
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    iban: '',
    swift_code: '',
    order_accept_type: 'Manual',
    status: true,
  });

  useEffect(() => {
    if (id) getSingleData();
    getCategories();
    getBusinessTypes();
  }, [id]);

  const getSingleData = async () => {
    try {
      setPageLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/partners/${id}`,
        data: { get_for: 'edit' }
      });
      const data = response.data;
      if (data.status) {
        const partner = data.data;
        delete partner.password;
        // Flatten nested bank_details back into the flat form fields
        const bank = partner.bank_details || {};
        partner.bank_name = bank.bank_name || '';
        partner.account_holder_name = bank.account_title || '';
        partner.account_number = bank.account_number || '';
        partner.iban = bank.iban || '';
        partner.swift_code = bank.swift_code || '';
        partner.business_type = partner.business_type_id || '';
        setFormData(partner);
        if (Array.isArray(partner.cover_images)) {
          setOldCoverImages(partner.cover_images);
        }
        if (partner.address || (partner.lat && partner.lng)) {
          setMapLocation({
            text: partner.address || '',
            lat: partner.lat || null,
            lng: partner.lng || null,
          });
        }
      } else {
        toast.error(data.message);
      }
      setPageLoader(false);
    } catch (error) {
      setPageLoader(false);
    }
  };

  const getCategories = async () => {
    try {
      const response = await ApiService.request({ method: 'GET', url: `admin/categories-list` });
      const data = response.data;
      if (data.status) setCategories(data.data);
    } catch (error) { /* silent */ }
  };

  const getBusinessTypes = async () => {
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: 'admin/business-types',
        data: { limit: 200 },
      });
      const data = response.data;
      if (data.status) setBusinessTypes(data.data || []);
    } catch (error) { /* silent */ }
  };

  const handleLocationChanged = useCallback((loc) => {
    setMapLocation(loc);
    setFormData((prev) => ({
      ...prev,
      address: loc.text,
      lat: loc.lat,
      lng: loc.lng,
    }));
  }, []);

  const handleImageChange = (name, file) => {
    setFormData((prev) => ({ ...prev, [name]: file }));
  };

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleDocChange = (key, file) =>
    setDocumentFiles((prev) => ({ ...prev, [key]: file }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.password && formData.password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoader(true);
    try {
      const form = new FormData();

      const skipKeys = new Set(['logo', 'cover_images', 'categories', 'emirates', 'documents',
        ...documentFields.map((d) => d.key)]);
      Object.entries(formData).forEach(([key, value]) => {
        if (skipKeys.has(key)) return;
        if (value === null || value === undefined) return;
        form.append(key, value);
      });

      (formData.categories || []).forEach((catId) => form.append('categories[]', catId));

      (formData.emirates || []).forEach((emirate) => form.append('emirates[]', emirate));

      if (formData.logo instanceof File) {
        form.append('logo', formData.logo);
      }

      oldCoverImages.forEach((url) => form.append('old_cover_images[]', url));

      coverFiles.forEach((file) => {
        if (file instanceof File) form.append('cover_images', file);
      });

      documentFields.forEach(({ key }) => {
        const file = documentFiles[key];
        if (file instanceof File) form.append(key, file);
      });

      const response = await ApiService.request({
        method: 'POST',
        url: `admin/partners/createOrUpdate/${id || ''}`,
        data: form,
      });

      const data = response.data;
      if (data.status) {
        toast.success(data.message);
        navigate('/admin/partners');
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  };

  const descLen = (formData.about || '').length;

  if (pageLoader) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Loader text="Loading…" size={40} />
      </div>
    );
  }

  return (
    <div className="partner-form-page">
      {/* Header */}
      <div className="pf-page-header">
        <div>
          <h1 className="pf-page-title">{id ? 'Edit' : 'Add New'} Partner</h1>
          <div className="pf-breadcrumb">
            <Link to="/admin/dashboard">Dashboard</Link>
            <i className="bi bi-chevron-right"></i>
            <Link to="/admin/partners">Partners</Link>
            <i className="bi bi-chevron-right"></i>
            <span>{id ? 'Edit' : 'Add New'}</span>
          </div>
        </div>
      </div>

      <form encType="multipart/form-data" method="POST" onSubmit={handleSubmit}>
        <div className="pf-grid">
          {/* ── Left column ── */}
          <div className="pf-col-left">

            {/* Business Information */}
            <section className="pf-card">
              <h6 className="pf-card-title">Business Information</h6>

              <div className="pf-grid-3">
                <div className="pf-field">
                  <label className="pf-label">Business Name <span className="pf-req">*</span></label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter business name"
                    required
                    value={formData.company_name || ''}
                    onChange={(e) => setField('company_name', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Business Type</label>
                  <div className="pf-select-wrap">
                    <select
                      className="pf-input pf-select"
                      value={formData.business_type || ''}
                      onChange={(e) => setField('business_type', e.target.value)}
                    >
                      <option value="">Select business type</option>
                      {businessTypes.map((bt) => (
                        <option key={bt._id} value={bt._id}>{bt.title}</option>
                      ))}
                    </select>
                    <i className="bi bi-chevron-down pf-select-caret"></i>
                  </div>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Company Email <span className="pf-req">*</span></label>
                  <input
                    type="email"
                    className="pf-input"
                    placeholder="Enter company email"
                    required
                    value={formData.email || ''}
                    onChange={(e) => setField('email', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Phone Number <span className="pf-req">*</span></label>
                  <div className="pf-phone">
                    <span className="pf-phone-prefix">🇦🇪 +971</span>
                    <input
                      type="text"
                      className="pf-input pf-phone-input"
                      placeholder="50 123 4567"
                      value={formData.phone || ''}
                      onChange={(e) => setField('phone', e.target.value)}
                    />
                  </div>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Website</label>
                  <input
                    type="url"
                    className="pf-input"
                    placeholder="Enter website (optional)"
                    value={formData.website || ''}
                    onChange={(e) => setField('website', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Owner Name</label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter owner name"
                    value={formData.owner_name || ''}
                    onChange={(e) => setField('owner_name', e.target.value)}
                  />
                </div>
              </div>

              {/* Short description */}
              <div className="pf-field pf-mt">
                <label className="pf-label">Short Description</label>
                <div className="pf-textarea-wrap">
                  <textarea
                    className="pf-input pf-textarea"
                    rows={3}
                    maxLength={250}
                    placeholder="Write a short description about the business"
                    value={formData.about || ''}
                    onChange={(e) => setField('about', e.target.value)}
                  />
                  <span className="pf-counter">{descLen} / 250</span>
                </div>
              </div>

              {/* Business address (map) */}
              <div className="pf-field pf-mt">
                <label className="pf-label">Business Address <span className="pf-req">*</span></label>
                <GoogleMapSelector
                  locationNew={mapLocation}
                  onLocationChanged={handleLocationChanged}
                />
              </div>
            </section>
            {/* Bank Details */}
            <section className="pf-card">
              <h6 className="pf-card-title">Bank Details</h6>
              <div className="pf-grid-3">
                <div className="pf-field">
                  <label className="pf-label">Bank Name</label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter bank name"
                    value={formData.bank_name || ''}
                    onChange={(e) => setField('bank_name', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Account Holder Name</label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter account holder name"
                    value={formData.account_holder_name || ''}
                    onChange={(e) => setField('account_holder_name', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Account Number</label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter account number"
                    value={formData.account_number || ''}
                    onChange={(e) => setField('account_number', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">IBAN</label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter IBAN"
                    value={formData.iban || ''}
                    onChange={(e) => setField('iban', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">SWIFT / BIC Code</label>
                  <input
                    type="text"
                    className="pf-input"
                    placeholder="Enter SWIFT / BIC code"
                    value={formData.swift_code || ''}
                    onChange={(e) => setField('swift_code', e.target.value)}
                  />
                </div>
              </div>
            </section>

            {/* Account Credentials */}
            <section className="pf-card">
              <h6 className="pf-card-title">Account Credentials</h6>
              {id && <p className="pf-help mb-3">Leave password fields empty to keep the existing password.</p>}
              <div className="pf-grid-3">
                <div className="pf-field">
                  <label className="pf-label">Login Email <span className="pf-req">*</span></label>
                  <input
                    type="email"
                    className="pf-input"
                    placeholder="Enter login email"
                    required
                    value={formData.email || ''}
                    onChange={(e) => setField('email', e.target.value)}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Password {!id && <span className="pf-req">*</span>}</label>
                  <div className="pf-password">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="pf-input"
                      placeholder="Enter password"
                      value={formData.password || ''}
                      onChange={(e) => setField('password', e.target.value)}
                    />
                    <button type="button" className="pf-eye" onClick={() => setShowPassword((v) => !v)}>
                      <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                    </button>
                  </div>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Confirm Password {!id && <span className="pf-req">*</span>}</label>
                  <div className="pf-password">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className="pf-input"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button type="button" className="pf-eye" onClick={() => setShowConfirm((v) => !v)}>
                      <i className={`bi bi-eye${showConfirm ? '-slash' : ''}`}></i>
                    </button>
                  </div>
                </div>
              </div>
            </section>

          </div>

          {/* ── Right column ── */}
          <div className="pf-col-right">

            {/* Profile & Gallery */}
            <section className="pf-card">
              <h6 className="pf-card-title">Profile &amp; Gallery</h6>

              <div className="pf-field">
                <label className="pf-label">Upload Logo</label>
                <ImageUpload
                  name="logo"
                  onImageChange={handleImageChange}
                  defaultPreviewUrl={formData.logo}
                />
              </div>

              <div className="pf-field pf-mt">
                <label className="pf-label">Gallery Images</label>
                <FileDropzone
                  oldFiles={oldCoverImages}
                  updateOldImages={setOldCoverImages}
                  setFiles={setCoverFiles}
                />
                <span className="pf-help">Upload up to 5 images</span>
              </div>
            </section>

            {/* Documents */}
            <section className="pf-card">
              <h6 className="pf-card-title">Upload Documents</h6>
              {documentFields.map(({ key, label }) => {
                const newFile = documentFiles[key];
                const existing = typeof formData[key] === 'string' ? formData[key] : null;
                return (
                  <div className="pf-field pf-mt" key={key}>
                    <label className="pf-label">{label}</label>
                    <div className="pf-doc-upload">
                      <label className="pf-doc-btn" htmlFor={`doc-${key}`}>
                        <i className="bi bi-upload me-2"></i>
                        {newFile ? 'Change file' : 'Choose file'}
                      </label>
                      <input
                        id={`doc-${key}`}
                        type="file"
                        className="pf-doc-input"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => handleDocChange(key, e.target.files[0] || null)}
                      />
                      <span className="pf-doc-name">
                        {newFile ? (
                          <>
                            <i className="bi bi-file-earmark-text me-1"></i>
                            {newFile.name}
                            <button
                              type="button"
                              className="pf-doc-clear"
                              onClick={() => handleDocChange(key, null)}
                              aria-label="Remove file"
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : existing ? (
                          <a href={existing} target="_blank" rel="noopener noreferrer">
                            <i className="bi bi-file-earmark-text me-1"></i>View current
                          </a>
                        ) : (
                          <span className="pf-doc-empty">No file selected</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
              <span className="pf-help">PDF, DOC, or image for each document.</span>
            </section>

            {/* Categories */}
            <section className="pf-card">
              <h6 className="pf-card-title">Services &amp; Categories</h6>
              <div className="pf-field">
                <label className="pf-label">Select Categories <span className="pf-req">*</span></label>
                <MultiSelect
                  value={formData.categories || []}
                  options={categories}
                  optionLabel="title"
                  optionValue="_id"
                  onChange={(e) => setField('categories', e.value)}
                  placeholder="Select categories"
                  filter
                  className="pf-multiselect"
                />
              </div>

              <div className="pf-field pf-mt">
                <label className="pf-label">Service Locations (Emirates)</label>
                <MultiSelect
                  value={formData.emirates || []}
                  options={emiratesOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => setField('emirates', e.value)}
                  placeholder="Select emirates"
                  filter
                  className="pf-multiselect"
                />
              </div>
            </section>

            {/* Order Settings */}
            <section className="pf-card">
              <h6 className="pf-card-title">Order Settings</h6>
              <div className="pf-field">
                <label className="pf-label">Order Accept Type</label>
                <div className="pf-select-wrap">
                  <select
                    className="pf-input pf-select"
                    value={formData.order_accept_type || 'Manual'}
                    onChange={(e) => setField('order_accept_type', e.target.value)}
                  >
                    <option value="Manual">Manual</option>
                    <option value="Auto">Auto</option>
                  </select>
                  <i className="bi bi-chevron-down pf-select-caret"></i>
                </div>
                <span className="pf-help">
                  Auto: orders are accepted automatically. Manual: the partner must accept each order.
                </span>
              </div>
            </section>

            {/* Account Status */}
            <section className="pf-card">
              <h6 className="pf-card-title">Account Status</h6>
              <div className="pf-status-row">
                <span className="pf-label mb-0">Status</span>
                <label className="pf-switch">
                  <input
                    type="checkbox"
                    checked={formData.status ?? true}
                    onChange={(e) => setField('status', e.target.checked)}
                  />
                  <span className="pf-switch-slider"></span>
                </label>
                <span className={`pf-status-text ${formData.status ? 'on' : 'off'}`}>
                  {formData.status ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="pf-help mt-2">You can change status later from the partner profile.</p>
            </section>
          </div>
        </div>

        {/* Actions */}
        <div className="pf-actions">
          <button type="button" className="pf-btn-cancel" onClick={() => navigate('/admin/partners')}>
            Cancel
          </button>
          <button type="submit" className="pf-btn-save" disabled={isLoader}>
            {isLoader ? (
              <Loader size={18} text="Saving" />
            ) : (
              <><i className="bi bi-save me-2"></i>Save Partner</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnerForm;
